import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';

interface OrderFormProps {
  preSelectedItems?: string[];
}

interface OrderItem {
  name: string;
  quantity: number;
}

export default function OrderForm({ preSelectedItems = [] }: OrderFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    deliveryDate: '',
    notes: ''
  });
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  useEffect(() => {
    if (preSelectedItems.length > 0) {
      const items = preSelectedItems.map(item => ({
        name: item,
        quantity: 1
      }));
      setOrderItems(items);
    }
  }, [preSelectedItems]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const increaseQuantity = (index: number) => {
    const newItems = [...orderItems];
    newItems[index].quantity += 1;
    setOrderItems(newItems);
    updateLocalStorage(newItems);
  };

  const decreaseQuantity = (index: number) => {
    const newItems = [...orderItems];
    if (newItems[index].quantity > 1) {
      newItems[index].quantity -= 1;
      setOrderItems(newItems);
      updateLocalStorage(newItems);
    }
  };

  const removeItem = (index: number) => {
    const newItems = orderItems.filter((_, i) => i !== index);
    setOrderItems(newItems);
    updateLocalStorage(newItems);
  };

  const updateLocalStorage = (items: OrderItem[]) => {
    const itemNames = items.map(item => item.name);
    localStorage.setItem('selectedOrderItems', JSON.stringify(itemNames));
    window.dispatchEvent(new Event('orderItemsUpdated'));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');
    
    const orderItemsText = orderItems
      .map(item => `${item.name} (Quantity: ${item.quantity})`)
      .join('\n');
    
    const submitData = {
      ...formData,
      orderItems: orderItemsText
    };
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4ok3rviu80n4l8qnhs0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(submitData).toString()
      });
      
      if (response.ok) {
        setSubmitStatus('Thank you! Your order has been submitted successfully.');
        
        // Clear localStorage after order submission
        localStorage.removeItem('selectedOrderItems');
        window.dispatchEvent(new Event('orderItemsUpdated'));
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          deliveryDate: '',
          notes: ''
        });
        setOrderItems([]);
      } else {
        setSubmitStatus('Sorry, there was an error submitting your order. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('Sorry, there was an error submitting your order. Please try again.');
    }
    
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 md:p-12" data-readdy-form id="order-form">
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
              placeholder="Your full name"
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
              placeholder="your.email@example.com"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
              placeholder="Your phone number"
            />
          </div>
          
          <div>
            <label htmlFor="deliveryDate" className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Delivery Date
            </label>
            <input
              type="date"
              id="deliveryDate"
              name="deliveryDate"
              value={formData.deliveryDate}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
            Delivery Address *
          </label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
            placeholder="Street address, city, region"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Order Items * {orderItems.length > 0 && `(${orderItems.length} items)`}
          </label>
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 min-h-[200px]">
            {orderItems.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                <i className="ri-shopping-cart-line text-4xl mb-2"></i>
                <p className="text-sm">No items selected yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {orderItems.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200 hover:border-yellow-400 transition-colors"
                  >
                    <div className="flex-1 text-sm font-medium text-gray-800">
                      {item.name}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => decreaseQuantity(index)}
                        className="w-7 h-7 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-md transition-colors cursor-pointer"
                      >
                        <i className="ri-subtract-line text-gray-700"></i>
                      </button>
                      <span className="w-10 text-center text-sm font-semibold text-gray-800">
                        {item.quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => increaseQuantity(index)}
                        className="w-7 h-7 flex items-center justify-center bg-yellow-400 hover:bg-yellow-500 rounded-md transition-colors cursor-pointer"
                      >
                        <i className="ri-add-line text-black"></i>
                      </button>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="w-7 h-7 flex items-center justify-center bg-red-500 hover:bg-red-600 rounded-md transition-colors ml-2 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-white"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {orderItems.length === 0 && (
            <p className="text-xs text-red-500 mt-2">
              Please select items from our collections before submitting your order
            </p>
          )}
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            maxLength={500}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm resize-none"
            placeholder="Any special instructions or requests..."
          ></textarea>
          <div className="text-right text-xs text-gray-500 mt-1">
            {formData.notes.length}/500 characters
          </div>
        </div>

        <Button 
          type="submit" 
          variant="primary" 
          size="lg" 
          className="w-full"
          disabled={isSubmitting || orderItems.length === 0}
        >
          {isSubmitting ? (
            <>
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Submitting Order...
            </>
          ) : (
            <>
              <i className="ri-shopping-cart-line mr-2"></i>
              Submit Order
            </>
          )}
        </Button>

        {submitStatus && (
          <div className={`text-center p-4 rounded-lg ${
            submitStatus.includes('Thank you') 
              ? 'bg-green-100 text-green-800 border border-green-200' 
              : 'bg-red-100 text-red-800 border border-red-200'
          }`}>
            {submitStatus}
          </div>
        )}
      </div>
    </form>
  );
}
